var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlert } from 'components/alert/AlertStack';
import { classNames, useQuery, useState } from 'libs/react';
import { useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import { FormAction } from 'components/form/FormAction';
import { FormField } from 'components/form/FormField';
import { FormGroup } from 'components/form/FormGroup';
import { FormInput } from 'components/form/FormInput';
import { FormSection } from 'components/form/FormSection';
import { CollapsibleFormSection } from 'components/form/CollapsibleFormSection';
import { MutationForm } from 'components/form/graphql/MutationForm';
import { CreateClientMutation } from 'graphql/Client/mutations/CreateClientMutation';
import { React } from 'libs/react';
import { useAccount } from 'components/account/AccountProvider';
import { useTranslation } from 'components/translation/TranslationProvider';
import { FormGoogleAutocompleteInput } from 'components/form/FormGoogleAutocompleteInput';
import { CustomFieldForm } from 'components/field/form/CustomFieldForm';
import { FieldGroupsQuery } from 'graphql/FieldGroup/queries/FieldGroupsQuery';
import { UpdateFieldValuesMutation } from 'graphql/FieldValue/mutations/UpdateFieldValuesMutation';
import { formatFieldValueWithTranslation } from 'helpers/Field';
import { getFilledFieldValues } from 'helpers/Field';
import './CreateClientPanel.scss';
/**
 * @component CreateClientPanel
 * @since 1.0.0
 */
export function CreateClientPanel(props) {
    var _a;
    const { style } = Object.assign({ style: 'normal' }, props);
    const alert = useAlert();
    const account = useAccount();
    const { customLabels, language } = useTranslation();
    const [location, setLocation] = useState(null);
    const [fieldGroupsResult] = useQuery(FieldGroupsQuery, {
        variables: {
            input: {
                query: [{ field: 'category', value: 'CLIENT' }]
            }
        }
    });
    const fieldGroups = (_a = fieldGroupsResult === null || fieldGroupsResult === void 0 ? void 0 : fieldGroupsResult.fieldGroups) !== null && _a !== void 0 ? _a : [];
    const [updateFieldValues] = useMutation(UpdateFieldValuesMutation);
    /**
     * @function onProcess
     * @since 1.0.0
     * @hidden
     */
    function onProcess(values) {
        const { name } = values;
        const input = {
            name,
            isEndClient: true,
            location
        };
        return {
            variables: {
                input,
                isEndClient: true
            },
            update(cache) {
                cache.evict({ fieldName: 'clients' });
            }
        };
    }
    /**
     * @function onSuccess
     * @since 1.0.0
     * @hidden
     */
    function onSuccess(result, values) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            alert.present({
                title: (React.createElement("p", null,
                    customLabels['Client'][language],
                    ' ',
                    React.createElement("strong", null, (_a = result === null || result === void 0 ? void 0 : result.createClient) === null || _a === void 0 ? void 0 : _a.name),
                    " ",
                    t('has been created.')))
            });
            const filledFields = getFilledFieldValues(values === null || values === void 0 ? void 0 : values.fields);
            if (Object.keys(filledFields).length > 0) {
                try {
                    yield updateFieldValues({
                        variables: {
                            input: {
                                fieldEntity: {
                                    entity: 'CLIENT',
                                    entityId: result.createClient.id
                                },
                                values: formatFieldValueWithTranslation(filledFields, undefined, language, account.languages)
                            }
                        }
                    });
                }
                catch (error) {
                    alert.present({ error: error });
                }
            }
            if (props.onDone) {
                props.onDone(result.createClient);
            }
        });
    }
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    function onFailure(error) {
        alert.present({ error });
    }
    const className = classNames('create-client-panel', 'create-client-panel--' + style, props.className);
    return (React.createElement("div", { className: className },
        React.createElement(MutationForm, { name: "client-create-form", mutation: CreateClientMutation, onProcess: onProcess, onSuccess: onSuccess, onFailure: onFailure, style: style },
            React.createElement(FormSection, { title: t(`What's the name of the company?`), style: style },
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormInput, { type: "text", name: "name", label: t('Name'), placeholder: t('Enter Name'), required: true }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormGoogleAutocompleteInput, { name: "address", label: t('Address'), placeholder: t('Enter Address'), value: location
                                ? (location === null || location === void 0 ? void 0 : location.civicNumber) +
                                    ' ' +
                                    (location === null || location === void 0 ? void 0 : location.road) +
                                    ', ' +
                                    (location === null || location === void 0 ? void 0 : location.city) +
                                    ', ' +
                                    (location === null || location === void 0 ? void 0 : location.province) +
                                    ', ' +
                                    (location === null || location === void 0 ? void 0 : location.country)
                                : '', onLocationSelected: (location) => {
                                setLocation(location);
                            } })))),
            fieldGroups.length > 0 && (React.createElement(CollapsibleFormSection, { title: t('Custom fields') },
                React.createElement(CustomFieldForm, { entity: "CLIENT", fields: fieldGroups, translable: false }))),
            React.createElement(FormAction, { align: "stretch" },
                React.createElement(Button, { style: "primary", label: t('Create company'), badge: "arrow-right", submit: true })))));
}
